import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { Suspense, lazy } from 'react';

import { NotFound } from 'components';
import ProtectedRoute from 'routers/ProtectedRoute';
import NewForm from 'containers/realtor/NewForm';

import type { HomelloNotification } from 'types';
import LinearProgress from 'components/LinearProgress';

const Templates = lazy(() => import('pages/realtor/Templates'));
const Transactions = lazy(() => import('pages/realtor/Transactions'));
const RealtorProfile = lazy(() => import('pages/realtor/RealtorProfile'));
const RealtorSettings = lazy(() => import('pages/realtor/RealtorSettings'));
const TransactionData = lazy(() => import('pages/realtor/TransactionData'));
const FormBuilder = lazy(() => import('pages/realtor/FormBuilder'));
const FAQ = lazy(() => import('pages/realtor/FAQ'));
const ContactUs = lazy(() => import('pages/realtor/ContactUs'));

export const REALTOR_NESTED_ROUTES = {
  newForm: '/newForm' as const,
  formBuilderRoot: '/formBuilder' as const,
  formBuilder: '/formBuilder/:formId' as const,
  templates: '/templates' as const,
  settings: '/settings' as const,
  profile: '/profile' as const,
  transactionData: '/transactionData' as const,
  faq: '/FAQ' as const,
  contact: '/contact' as const
};

interface Props {
  notifications: HomelloNotification[] | null;
}

export default function RealtorNestedRoutes({ notifications }: Props): JSX.Element {
  const { path } = useRouteMatch();

  return (
    <Suspense fallback={<LinearProgress />}>
      <Switch>
        <ProtectedRoute role="realtor" exact path={`${path}`}>
          <Transactions notifications={notifications ? notifications : null} />
        </ProtectedRoute>
        <ProtectedRoute
          role="realtor"
          exact
          path={`${path}${REALTOR_NESTED_ROUTES.formBuilderRoot}`}
        >
          <NewForm />
        </ProtectedRoute>
        <ProtectedRoute role="realtor" exact path={`${path}${REALTOR_NESTED_ROUTES.newForm}`}>
          <NewForm newForm={true} />
        </ProtectedRoute>
        <ProtectedRoute role="realtor" path={`${path}${REALTOR_NESTED_ROUTES.formBuilder}`}>
          <FormBuilder />
        </ProtectedRoute>
        <ProtectedRoute role="realtor" path={`${path}${REALTOR_NESTED_ROUTES.templates}`}>
          <Templates />
        </ProtectedRoute>
        <ProtectedRoute role="realtor" path={`${path}${REALTOR_NESTED_ROUTES.profile}`}>
          <RealtorProfile />
        </ProtectedRoute>
        <ProtectedRoute role="realtor" path={`${path}${REALTOR_NESTED_ROUTES.settings}`}>
          <RealtorSettings />
        </ProtectedRoute>
        <ProtectedRoute role="realtor" path={`${path}${REALTOR_NESTED_ROUTES.transactionData}`}>
          <TransactionData />
        </ProtectedRoute>
        <ProtectedRoute role="realtor" path={`${path}${REALTOR_NESTED_ROUTES.faq}`}>
          <FAQ />
        </ProtectedRoute>
        <ProtectedRoute role="realtor" path={`${path}${REALTOR_NESTED_ROUTES.contact}`}>
          <ContactUs />
        </ProtectedRoute>
        <Route>
          <NotFound text="The requested page was not found" />
        </Route>
      </Switch>
    </Suspense>
  );
}
